import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Row, Table, ButtonGroup } from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import {
   FilterDateTime, FilterSelect, FilterSwitch, FilterText,
   ItemTypeahead, Pager, ServiceThreadSpinner, SortHeader,
} from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from "../utils";

export default function ConsignedInventoryInquiry({ short, ...props }) {
   const { currentUser } = useContext(UserContext);
   const [consignedCustomers, setConsignedCustomers] = useState("");
   const [customerItems, setCustomerItems] = useState([]);
   const [data, setData] = useState([]);
   const [downloading, setDownloading] = useState(false);
   const [emptyCriteria, setEmptyCriteria] = useState(false);
   const [filters, setFilters] = useState([
      { filterName: "startDate", value: null },
      { filterName: "endDate", value: null },
      { filterName: "CustomerId", value: "" },
      { filterName: "ItemNumber", value: "" },
      { filterName: "openTransfers", value: false },
   ]);
   const [initialized, setInitialized] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(25);
   const [sortDirection, setSortDirection] = useState("desc");
   const [sortField, setSortField] = useState("documentDate");
   const [totalPages, setTotalPages] = useState();
   const navigate = useNavigate();
   //const today = new Date();

   useEffect(() => {
      document.title = `Consigned Inventory Shipments`;
      if (!initialized && currentUser) {
         if (currentUser.isConsignedCustomerRole) {
            //const filterCopy = _.slice(filters);
            setInitialized(true);
         }
         api
            .fetch(`Customer/LoadCISDropdowns`)
            .then((response) => {
               if (response.data.success) {
                  setConsignedCustomers(
                     _.concat(
                        [{ value: "", label: "No Restriction" }],
                        _.map(response.data.customers, (cst) => {
                           return {
                              value: cst.customerId,
                              label: cst.customerNumName,
                           };
                        })
                     )
                  );
                  setCustomerItems(
                     _.map(response.data.customerItems, (item) => {
                        return { value: item.ddlValue, label: item.ddlText };
                     })
                  );
               }
            })
            .catch(api.catchHandler)
            .finally(() => setInitialized(true));
      }

      if (initialized && !loaded && !downloading) {
         runQuery();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded, downloading, currentUser]);

   function createPayload() {
      let payload = {
         CustomerId: "",
         SortField: sortField,
         SortDirection: sortDirection,
         pageNumber: page,
         pageSize: pageSize,
      };
      _.map(filters, (filter) => (payload[filter.filterName] = filter.value));
      return payload;
   }

   function currentDate() {
      let date = new Date();
      let month = date.getMonth() + 1;
      let day = date.getDate();
      let year = date.getFullYear();
      return `${month}-${day}-${year}`;
   }

   function exportXlsx() {
      setDownloading(true);
      api
         .postGetBlob("Customer/ExportConsignedTransfersXLSX", createPayload())
         .then((response) => {
            setDownloading(false);
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = `Consigned_Inventory_Transfers.xlsx`;
                  helpers.downloadExport(
                     response.data,
                     XLSX_FILENAME,
                     constants.MIME_TYPES.xlsx
                  );
               }
            } else {
               toast.error(response.message || "There was an error downloading the invoice history");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error("There was an error downloading the invoice history");
         })
         .finally(() => setDownloading(false));
   }

   function handleConsignedInventoryReport(documentNumber) {
      setDownloading(true);
      api
         .postDownloadBinary(
            "Report/ConsignedInventoryTransfers",
            {
               DocumentNumber: documentNumber,
               SortField: sortField,
               SortDirection: sortDirection,
            },
            constants.MIME_TYPES.pdf,
            `Consigned_Inventory_Transfers_${documentNumber}_${currentDate()}`
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
      setTotalPages(null);
      onPageChange(1);
      setLoaded(false);
   }

   function onPageChange(page) {
      setPage(page);
      setLoaded(false);
   }

   function onPageSizeChange(size) {
      setPageSize(size);
      setTotalPages(null);
      onPageChange(1);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
      setLoaded(false);
   }

   function runQuery() {
      var payload = createPayload();
      if (
         _.isNil(payload.CustomerId) &&
         !payload.endDate &&
         !payload.startDate &&
         !payload.openTransfers &&
         !payload.ItemNumber &&
         !payload.cusomterPONumber
      ) {
         setEmptyCriteria(true);
         setLoaded(true);
         setData([]);
         setTotalPages(null);
         return;
      } else {
         setEmptyCriteria(false);
      }
      if (currentUser.isConsignedCustomerRole) {
         payload.CustomerId = "FLEX05"
         api
            .post(`Customer/GetConsignedInventoryTransfersCust`, payload)
            .then((response) => {
               if (response.data.success) {
                  setData(response.data.transfers.items);
                  setTotalPages(_.ceil(response.data.transfers.totalCount / pageSize));
               }
            })
            .catch(api.catchHandler)
            .finally(() => setLoaded(true));
      }
      else {
         api
            .post(`Customer/GetConsignedInventoryTransfers`, payload)
            .then((response) => {
               if (response.data.success) {
                  setData(response.data.transfers.items);
                  setTotalPages(_.ceil(response.data.transfers.totalCount / pageSize));
               }
            })
            .catch(api.catchHandler)
            .finally(() => setLoaded(true));
      }
   }

   if (short) {
      return (
         <>
            <Row className="justify-content-center">
               <Col xs={6}>
                  {currentUser && !currentUser.isAnyCustomerTypeRole && (
                     <Row className="align-items-center mt-2">
                        <Col className="text-end  h5" xs={4}>
                           Customer:
                        </Col>
                        <Col xs={8}>
                           <FilterSelect
                              filterName="CustomerId"
                              options={consignedCustomers}
                              value={filterHelpers.getValue(filters, "CustomerId")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                     </Row>
                  )}
                  <Row className="align-items-center mt-2">
                     <Col className="text-end  h5" xs={4}>
                        Item #:
                     </Col>
                     <Col xs={8}>
                        {/* <ItemTypeahead
                           onChange={(e) => {
                              onFilterChange({
                                 filterName: "ItemNumber",
                                 value: e === null ? null : e.itemNumber,
                                 label: e.label,
                                 filteredDisplay:
                                 e === null ? "" : `Item Number: ${e.label}`,
                              });
                           }}
                           /> */}
                        <FilterSelect
                           filterName="ItemNumber"
                           options={customerItems}
                           value={filterHelpers.getValue(filters, "ItemNumber")}
                           onChangeCallback={onFilterChange}
                           isClearable
                        />
                     </Col>
                  </Row>
                  <Row className="align-items-center mt-2">
                     <Col className="text-end  h5" xs={4}>
                        PO:
                     </Col>
                     <Col xs={8}>
                        <FilterText
                           filterName="customerPoNumber"
                           value={filterHelpers.getValue(filters, "customerPoNumber")}
                           onChangeCallback={(e) => onFilterChange(e)}
                        />
                     </Col>
                  </Row>
               </Col>
               <Col xs={6}>
                  <Row className="align-items-center mt-2">
                     <Row className="align-items-center mt-2">
                        <Col className="text-end  h5" xs={4}>
                           Start Ship Date:
                        </Col>
                        <Col xs={6}>
                           <FilterDateTime
                              filterName="startDate"
                              value={filterHelpers.getValue(filters, "startDate")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                        <Col xs={2}>
                           {filterHelpers.getValue(filters, "startDate") && (
                              <Button
                                 className="shredButton"
                                 onClick={() =>
                                    onFilterChange({
                                       filterName: "startDate",
                                       value: null,
                                       filteredDisplay: null,
                                    })
                                 }
                              >
                                 Clear
                              </Button>
                           )}
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col className="text-end  h5" xs={4}>
                           End Ship Date:
                        </Col>
                        <Col xs={6}>
                           <FilterDateTime
                              filterName="endDate"
                              value={filterHelpers.getValue(filters, "endDate")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                        <Col xs={2}>
                           {filterHelpers.getValue(filters, "startDate") && (
                              <Button
                                 className="shredButton"
                                 onClick={() =>
                                    onFilterChange({
                                       filterName: "startDate",
                                       value: null,
                                       filteredDisplay: null,
                                    })
                                 }
                              >
                                 Clear
                              </Button>
                           )}
                        </Col>
                     </Row>
                     <Row className="mt-2">
                        <Col className="text-end  h5" xs={4}>
                           Open Transfers Only:
                        </Col>
                        <Col xs={8}>
                           <FilterSwitch
                              filterName="openTransfers"
                              value={filterHelpers.getValue(filters, "openTransfers")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                     </Row>
                  </Row>
               </Col>
            </Row>
            <Row className="mb-5">
               <Col xxl={{ size: 9, offset: 2 }}>
                  {data.length ? (
                     <ServiceThreadSpinner on={!loaded}>
                        <Row className="align-items-center mt-3 mb-2">
                           <Col className="transparent-background">
                              <Pager
                                 callBack={onPageChange}
                                 sizeCallback={onPageSizeChange}
                                 pageNumber={page}
                                 pageSize={pageSize ? pageSize : null}
                                 totalPages={totalPages}
                                 downloading={downloading}
                              />
                           </Col>
                           <Col className="transparent-background">
                              <ButtonGroup className="float-end">
                                 <Button
                                    title="Download as .xlsx"
                                    className="shredButtonExcel float-end ms-2"
                                    onClick={exportXlsx}
                                    disabled={downloading}
                                 >
                                    <FontAwesomeIcon icon="file-excel" size="lg" />
                                 </Button>
                              </ButtonGroup>
                           </Col>
                        </Row>
                        <Table className="data-table">
                           <thead className="table-header">
                              <tr>
                                 <th>
                                    <SortHeader
                                       displayName="Packing List"
                                       field="documentNumber"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "documentNumber"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th>
                                    <SortHeader
                                       displayName="Document Date"
                                       field="documentDate"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "documentDate"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th>
                                    <SortHeader
                                       displayName="Location"
                                       field="location"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "location"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th>
                                    <SortHeader
                                       displayName="Customer #"
                                       field="customerId"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "customerId"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th>
                                    <SortHeader
                                       displayName="Customer Name"
                                       field="customerName"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "customerName"}
                                       callback={onSortChange}
                                    />
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {_.map(data, (datum, index) => {
                                 return (
                                    <tr
                                       key={index}
                                       className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                                    >
                                       <td className="data-cell">
                                          <Button
                                             className="shredLink"
                                             children={datum.documentNumber}
                                             onClick={() => handleConsignedInventoryReport(datum.documentNumber)}
                                          />
                                       </td>
                                       <td
                                          className="data-cell"
                                          children={dateHelpers.formatDateToShortDate(datum.documentDate)}
                                       />
                                       <td className="data-cell" children={datum.location} />
                                       <td
                                          className="data-cell"
                                          children={datum.customerId ? datum.customerId : "Unknown"}
                                       />
                                       <td
                                          className="data-cell"
                                          children={datum.customerName ? datum.customerName : "Unknown"}
                                       />
                                    </tr>
                                 );
                              })}
                           </tbody>
                        </Table>
                     </ServiceThreadSpinner>
                  ) : (
                     <div className="h5 text-center mt-3 ">
                        {emptyCriteria
                           ? "Please fill in one or more criteria"
                           : "No data found"}
                     </div>
                  )}
               </Col>
            </Row>
         </>
      );
   } else {
      return (
         <DesktopContainer screenName="Consigned Inventory Shipment Inquiry">
            <div className="filter-header">
               <Row className="justify-content-center">
                  <Col xs={5}>
                     {currentUser && !currentUser.isAnyCustomerTypeRole && (
                        <Row className="align-items-center mt-2">
                           <Col className="text-end text-white h5" xs={3}>
                              Customer:
                           </Col>
                           <Col xs={8}>
                              <FilterSelect
                                 filterName="CustomerId"
                                 options={consignedCustomers}
                                 value={filterHelpers.getValue(filters, "CustomerId")}
                                 onChangeCallback={(e) => onFilterChange(e)}
                              />
                           </Col>
                        </Row>
                     )}
                     {currentUser && !currentUser.isAnyCustomerTypeRole ? (
                        <Row className="align-items-center mt-2">
                           <Col className="text-end text-white h5" xs={3}>
                              Item #:
                           </Col>
                           <Col xs={8}>
                              <ItemTypeahead
                                 onChange={(e) => {
                                    onFilterChange({
                                       filterName: "ItemNumber",
                                       value: e === null ? "" : e.itemNumber,
                                       label: e === null ? "" : e.itemNumber,
                                       filteredDisplay: e === null ? "" : `Item Number: ${e.itemNumber}`,
                                    });
                                 }}
                              />
                           </Col>
                        </Row>
                     ) :
                        <Row className="align-items-center mt-2">
                           <Col className="text-end text-white h5" xs={3}>
                              Item #:
                           </Col>
                           <Col xs={8}>
                              <FilterSelect
                                 filterName="ItemNumber"
                                 options={customerItems}
                                 value={filterHelpers.getValue(filters, "ItemNumber")}
                                 onChangeCallback={onFilterChange}
                                 isClearable
                              />
                           </Col>
                        </Row>}
                     <Row className="align-items-center mt-2">
                        <Col className="text-end text-white h5" xs={3}>
                           PO:
                        </Col>
                        <Col xs={8}>
                           <FilterText
                              filterName="customerPoNumber"
                              value={filterHelpers.getValue(filters, "customerPoNumber")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="mt-2">
                        <Col xs={9} className="offset-md-3">
                           {currentUser?.isAdmin ? (
                              <Button onClick={() => {
                                 navigate(constants.ENDPOINTS.customerCard, { state: null, replace: true,});
                              }}
                                 className="shredButton"
                              >
                                 Open Customer Consignee Card
                              </Button>
                           ) : null}
                        </Col>
                     </Row>
                  </Col>
                  <Col xs={7}>
                     <Row className="align-items-center mt-2">
                        <Col className="text-end text-white h5" xs={3}>
                           Start Ship Date:
                        </Col>
                        <Col xs={8}>
                           <FilterDateTime
                              filterName="startDate"
                              value={filterHelpers.getValue(filters, "startDate")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col className="text-end text-white h5" xs={3}>
                           End Ship Date:
                        </Col>
                        <Col xs={8}>
                           <FilterDateTime
                              filterName="endDate"
                              value={filterHelpers.getValue(filters, "endDate")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="mt-2">
                        <Col className="text-end text-white h5" xs={4}>
                           Open Transfers Only:
                        </Col>
                        <Col xs={8}>
                           <FilterSwitch
                              filterName="openTransfers"
                              value={filterHelpers.getValue(filters, "openTransfers")}
                              onChangeCallback={(e) => onFilterChange(e)}
                           />
                        </Col>
                     </Row>

                  </Col>
               </Row>
            </div>
            {data.length ? (
               <ServiceThreadSpinner on={!loaded}>
                  <Row className="align-items-center mt-3 mb-2">
                     <Col className="transparent-background">
                        <Pager
                           callBack={onPageChange}
                           sizeCallback={onPageSizeChange}
                           pageNumber={page}
                           pageSize={pageSize ? pageSize : null}
                           totalPages={totalPages}
                           downloading={downloading}
                        />
                     </Col>
                     <Col className="transparent-background">
                        <ButtonGroup className="float-end">
                           <Button
                              title="Download as .xlsx"
                              className="shredButtonExcel float-end ms-2"
                              onClick={exportXlsx}
                              disabled={downloading}
                           >
                              <FontAwesomeIcon icon="file-excel" size="lg" />
                           </Button>
                        </ButtonGroup>
                     </Col>
                  </Row>
                  <Table className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th>
                              <SortHeader
                                 displayName="Packing List"
                                 field="documentNumber"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "documentNumber"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Document Date"
                                 field="documentDate"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "documentDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Location"
                                 field="location"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "location"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Customer #"
                                 field="customerId"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "customerId"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Customer Name"
                                 field="customerName"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "customerName"}
                                 callback={onSortChange}
                              />
                           </th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(data, (datum, index) => {
                           return (
                              <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`} >
                                 <td className="data-cell">
                                    <Button
                                       className="shredLink"
                                       children={datum.documentNumber}
                                       onClick={() => handleConsignedInventoryReport(datum.documentNumber)}
                                    />
                                 </td>
                                 <td
                                    className="data-cell"
                                    children={dateHelpers.formatDateToShortDate(datum.documentDate)}
                                 />
                                 <td className="data-cell" children={datum.location} />
                                 <td
                                    className="data-cell"
                                    children={datum.customerId ? datum.customerId : "Unknown"}
                                 />
                                 <td
                                    className="data-cell"
                                    children={datum.customerName ? datum.customerName : "Unknown"}
                                 />
                              </tr>
                           );
                        })}
                     </tbody>
                  </Table>
               </ServiceThreadSpinner>
            ) : (
               <div className="h5 text-center mt-3">
                  {emptyCriteria
                     ? "Please fill in one or more criteria"
                     : "No data found"}
               </div>
            )}
         </DesktopContainer>
      );
   }
}
